package com.wintermar.inspection.feature.presentation.template.landing

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.wintermar.inspection.core.data.Uuid
import com.wintermar.inspection.core.domain.FlowResult
import com.wintermar.inspection.core.ui.component.datetime.util.DateTimeUtil
import com.wintermar.inspection.core.ui.component.snackbar.util.SnackBarController
import com.wintermar.inspection.core.ui.component.snackbar.util.SnackBarVariant
import com.wintermar.inspection.feature.domain.model.template.TemplateModel
import com.wintermar.inspection.feature.domain.usecase.inspection.CreateNewInspectionUseCase
import com.wintermar.inspection.feature.domain.usecase.inspection.StartInspectionUseCase
import com.wintermar.inspection.feature.domain.usecase.reference.GetProfileUseCase
import com.wintermar.inspection.feature.domain.usecase.template.GetListTemplateUseCase
import com.wintermar.inspection.feature.domain.usecase.template.GetTemplateByIdUseCase
import com.wintermar.inspection.feature.util.expect_init.processAfterSuccessStartInspection
import com.wintermar.inspection.feature.util.expect_init.processGetProfile
import com.wintermar.inspection.util.Constant
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.launchIn
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.launch
import kotlin.time.Duration.Companion.milliseconds

class TemplateLandingViewModel(
    private val getListTemplateUseCase: GetListTemplateUseCase,
    private val getTemplateByIdUseCase: GetTemplateByIdUseCase,
    private val createNewInspectionUseCase: CreateNewInspectionUseCase,
    private val startInspectionUseCase: StartInspectionUseCase,
    private val getProfileUseCase: GetProfileUseCase
) : ViewModel() {
    private val _navigationEvent = Channel<TemplateLandingEvent>()
    val navigationEvent = _navigationEvent.receiveAsFlow()

    val state = TemplateLandingState(
        onGetTemplate = { page, pageSize, isRefreshData ->
            getTemplates(page, pageSize, isRefreshData)
        },
        onGetDetailTemplate = { id, data ->
            getTemplateById(id, data)
        },
        onCreateNewInspection = {
            startInspection(it)
        }
    )

    init {
        onInit()
    }

    private fun onInit() = viewModelScope.launch {
        processGetProfile(getProfileUseCase)
        getTemplates(page = state.page, pageSize = state.pageSize, isRefreshData = false)
    }

    private fun getTemplates(page: Int, pageSize: Int, isRefreshData: Boolean) {
        getListTemplateUseCase(page, pageSize, isRefreshData).onEach { result ->
            when (result) {
                is FlowResult.Loading -> {
                    if (!result.showLoading && isRefreshData) delay(500.milliseconds)

                    if (isRefreshData) state.isRefreshing = result.showLoading
                    else state.isLoading = result.showLoading
                }

                is FlowResult.Success -> {
                    state.data = result.data ?: state.data
                }

                is FlowResult.Error -> {
                    SnackBarController.show(
                        message = result.error?.message ?: Constant.UNKNOWN_ERROR,
                        variant = SnackBarVariant.ERROR
                    )
                }
            }
        }.launchIn(viewModelScope)
    }

    private fun getTemplateById(id: String, data: TemplateModel) {
        getTemplateByIdUseCase(id, data).onEach { result ->
            when (result) {
                is FlowResult.Loading -> {
                    state.isLoadingDetailTemplate = result.showLoading
                }

                is FlowResult.Success -> {
                    state.detailTemplate = result.data ?: state.detailTemplate
                }

                is FlowResult.Error -> {
                    SnackBarController.show(
                        message = result.error?.message ?: Constant.UNKNOWN_ERROR,
                        variant = SnackBarVariant.ERROR
                    )
                }
            }
        }.launchIn(viewModelScope)
    }

    private fun startInspection(templateId: String) {
        val inspectionId = Uuid.generate()
        val conducted = DateTimeUtil.getCurrentDateIsoUtc0()
        startInspectionUseCase(
            templateId = templateId,
            inspectionId = inspectionId,
            conductedIso = conducted
        ).onEach {
            when (it) {
                is FlowResult.Loading -> {
                    state.isLoadingCreateInspection = it.showLoading
                }

                is FlowResult.Success -> processAfterSuccessStartInspection(
                    onCreateLocalData = {
                        createNewInspection(
                            templateId,
                            inspectionId,
                            conductedIso = conducted
                        )
                    },
                    onGoToInspectionAnswer = {
                        goToInspectionAnswer(inspectionId)
                    }
                )

                is FlowResult.Error -> SnackBarController.show(
                    message = it.error?.message ?: Constant.UNKNOWN_ERROR,
                    variant = SnackBarVariant.ERROR
                )
            }
        }.launchIn(viewModelScope)
    }

    private fun createNewInspection(
        templateId: String,
        inspectionId: String,
        conductedIso: String
    ) {
        createNewInspectionUseCase(
            templateId,
            inspectionId,
            conductedIso
        ).onEach { result ->
            when (result) {
                is FlowResult.Loading -> {
                    state.isLoadingCreateInspection = result.showLoading
                }

                is FlowResult.Success -> goToInspectionAnswer(inspectionId)

                is FlowResult.Error -> {
                    SnackBarController.show(
                        message = result.error?.message ?: Constant.UNKNOWN_ERROR,
                        variant = SnackBarVariant.ERROR
                    )
                }
            }
        }.launchIn(viewModelScope)
    }

    private suspend fun goToInspectionAnswer(inspectionId: String) {
        _navigationEvent.send(
            TemplateLandingEvent.NavigateToInspectionAnswer(
                inspectionId
            )
        )
    }
}
